'use client';

import { startTransition, useState } from 'react';

import NextImage from 'next/image';
import { usePathname } from 'next/navigation';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackNavigation } from '@domain/analytics/analytics';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { FeatureFlagNotification } from '../../FeatureFlagNotification/FeatureFlagNotification';
import { Heading } from '../../Heading/Heading';
import { SearchIcon } from '../../Icons/SearchIcon';
import { Logo } from '../../Logo/Logo';
import { SearchBox } from '../../SearchBox/SearchBox';

import { trackEvent } from '@domain/analytics/trackEvent';
import { useUserInfo } from '@domain/user/useUserInfo';
import activeLinkIcon from './assets/activeLinkIconBottom.svg';
import { DropDownMenu } from './components/DropDownMenu/DropDownMenu';
import { LoginButton } from './components/LoginButton/LoginButton';
import { SuspendedLoginButton } from './components/LoginButton/SuspendedLoginButton';
import { DESKTOP_MENU_ICON_HEIGHT, DESKTOP_MENU_ICON_WIDTH } from './constants/constants';
import { INavLinkItem, MAIN_NAV_LINKS } from './constants/navlinks';

/**
 * The **MainNavigation** component implements the main navigation of the app.
 * Through it, all sections and their variations are defined.
 * @returns A JSX element.
 */
export const MainNavigation = ({ isAppRouter = false }: { isAppRouter?: boolean }) => {
  const pathname = usePathname();
  const isBrands: boolean = pathname === '/brands/all';

  const { currentPage } = useAnalytics();
  const { isLoggedIn } = useUserInfo();
  const [expanded, setExpanded] = useState(false);

  const [isSearchBoxActive, setIsSearchBoxActive] = useState(false);

  const handleExpandedChange = (value: boolean): void => {
    startTransition(() => {
      setExpanded(value);
    });
  };

  return (
    <header
      className="z-nav border-b-twilight sticky top-0 min-h-(--main-navigation-height-mobile) w-full items-center border-b bg-white sm:min-h-(--main-navigation-height)"
      data-testid="MainNavigation"
    >
      <FeatureFlagNotification />
      <nav
        className={`relative flex items-center ${
          expanded ? 'justify-end md:justify-between' : 'justify-between'
        } min-h-(--main-navigation-height-mobile) px-6 py-3 sm:min-h-(--main-navigation-height) md:px-14 md:py-2.5 lg:px-16 xl:mx-auto`}
      >
        {!isBrands && isSearchBoxActive ? (
          <div className="ml-2 block grow">
            <SearchBox
              placeholder="Search on Fishbrain..."
              width="100%"
              widthMenu="100%"
              className="!h-8 sm:!h-10"
              parent="main-navigation"
              showCloseBtn
              onCloseSearchBox={() => {
                setExpanded(false);
                setIsSearchBoxActive(false);
              }}
            />
          </div>
        ) : (
          <>
            <div className="flex items-center">
              <div className={`m-px md:w-48 lg:mx-0 ${expanded ? 'hidden md:block' : ''}`}>
                <Logo />
              </div>
              <div className="hidden md:block">
                <ul className="m-0 flex items-center md:pl-16">
                  {MAIN_NAV_LINKS.map(({ path, title, id, subPages }: INavLinkItem) => {
                    const isActiveSubPage = subPages?.filter(page => pathname.includes(page));
                    const isActiveTab = pathname.includes(path) || isActiveSubPage?.length;
                    return (
                      <li key={`dropdown-secondary-nav-link-${id}`} className="m-0">
                        <InternalLink
                          variant="textNone"
                          to={path}
                          className="flex items-center"
                          onClick={() => {
                            trackNavigation({ path, currentPage });
                          }}
                        >
                          <Heading
                            as="span"
                            withDefaults={false}
                            variant="2xs"
                            color={isActiveTab ? 'navHighlight' : 'dark'}
                            className="font-heading hover:text-pinktail-shade flex flex-col items-center px-3"
                          >
                            {title}
                            <NextImage
                              alt="Active Link Icon"
                              width={21}
                              height={6}
                              className={`my-auto ${isActiveTab ? 'absolute bottom-0' : 'hidden'}`}
                              src={activeLinkIcon}
                            />
                          </Heading>
                        </InternalLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between space-x-6 sm:justify-center">
              <button
                aria-label="main search"
                className={`p-0.5 ${expanded ? 'hidden md:block' : ''}`}
                data-testid="main-search-icon"
                onClick={() => {
                  setIsSearchBoxActive(true);
                }}
                type="button"
              >
                <SearchIcon width={DESKTOP_MENU_ICON_WIDTH} height={DESKTOP_MENU_ICON_HEIGHT} />
              </button>
              {!isLoggedIn && (
                <div
                  className="hidden items-center justify-start md:flex"
                  data-testid="desktop log in button"
                >
                  {isAppRouter ? (
                    <SuspendedLoginButton
                      buttonSize="sm"
                      className="px-3 sm:px-5"
                      id="MainNavigationLoginButton"
                    />
                  ) : (
                    <LoginButton
                      onClick={() => {
                        trackEvent({
                          name: 'mykiss_hamburger_clicked',
                          properties: { action: 'navigate', source: currentPage },
                        });
                      }}
                      buttonSize="sm"
                      className="px-3 sm:px-5"
                    />
                  )}
                </div>
              )}
              <div className="relative block self-center">
                <DropDownMenu
                  isAppRouter={isAppRouter}
                  expanded={expanded}
                  onExpandedChange={handleExpandedChange}
                />
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};
